import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Download } from 'lucide-react';
import type { OnboardingTaskKind } from '@hcc/types';
import { api, ApiError } from '../../lib/api';
import { useCurrentUser } from '../../hooks/useAuth';
import { Hero } from '../../components/ui/Hero';
import { EmptyState } from '../../components/ui/EmptyState';
import { Card, CardSection } from '../../components/ui/Card';
import { SkeletonList } from '../../components/ui/Skeleton';
import { FormRenderer } from '../../components/onboarding/FormRenderer';
import { MyFormSubmission } from '../../components/onboarding/MyFormSubmission';

interface MeTask {
  task_id: string;
  title: string;
  description: string | null;
  status: 'pending' | 'awaiting_review' | 'changes_requested' | 'complete';
  onboarding_kind?: OnboardingTaskKind;
  form_id?: string;
}
interface MeView {
  tasks: MeTask[];
}

/**
 * Dedicated, focused page for filling in one onboarding form. Reached from
 * /me; clicking a form there navigates here so the inductee works through the
 * whole form on its own screen (no inline clutter). FormRenderer autosaves
 * answers as they go, so progress survives a refresh or accidental back.
 */
export function FormFill() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading: sessionLoading } = useCurrentUser();

  const me = useQuery({
    queryKey: ['me-view'],
    enabled: !!user?.person_id,
    queryFn: () => api<MeView>('/api/me'),
  });

  if (sessionLoading) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-8">
        <SkeletonList rows={4} />
      </main>
    );
  }
  // No session (expired or signed out): branded login page, not a stub.
  if (!user) return <Navigate to="/login" replace />;
  if (!user.person_id) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        This page is for attendees. <Link to="/" className="underline">Home</Link>.
      </main>
    );
  }
  if (me.isLoading) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-8">
        <SkeletonList rows={4} />
      </main>
    );
  }
  if (me.error) {
    const status = me.error instanceof ApiError ? me.error.status : 0;
    return (
      <main className="mx-auto max-w-2xl px-4 py-8">
        <EmptyState icon={<AlertCircle className="h-6 w-6" />} title="Couldn't load this form" description={`The server returned ${status}.`} />
      </main>
    );
  }

  const task = me.data?.tasks.find((t) => t.task_id === taskId);

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-2xl space-y-5 px-4 py-8 sm:px-6">
        <Link to="/me" className="inline-flex items-center gap-1 text-sm font-medium text-fg-muted hover:text-fg">
          <ArrowLeft className="h-4 w-4" />
          Back to your onboarding
        </Link>

        {!task || task.onboarding_kind !== 'form' || !task.form_id ? (
          <EmptyState
            icon={<AlertCircle className="h-6 w-6" />}
            title="Form not found"
            description="This form isn't assigned to you, or it's already complete."
          />
        ) : task.status === 'complete' || task.status === 'awaiting_review' ? (
          <>
            <Hero
              eyebrow={<span>Onboarding</span>}
              title={task.title}
              description={
                task.status === 'awaiting_review'
                  ? "Submitted — awaiting review. Here's what you sent."
                  : "Completed. Here's a read-only copy of what you submitted."
              }
            />
            <Card>
              <CardSection className="space-y-3">
                <a
                  href={`/api/me/onboarding/forms/${task.form_id}/signed.pdf`}
                  className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
                >
                  <Download className="h-4 w-4" />
                  Download signed PDF
                </a>
                <MyFormSubmission taskId={task.task_id} />
              </CardSection>
            </Card>
          </>
        ) : (
          <>
            <Hero
              eyebrow={<span>Onboarding</span>}
              title={task.title}
              description={task.description ?? 'Please complete the form below. Your answers save as you go.'}
            />
            <Card>
              <CardSection>
                <FormRenderer
                  taskId={task.task_id}
                  formId={task.form_id}
                  onDone={() => navigate('/me')}
                />
              </CardSection>
            </Card>
          </>
        )}
      </div>
    </main>
  );
}
